import * as THREE from 'three';

/**
 * Everything that helps the vendor pose a shot, and must never be in it.
 *
 * The studios hide their grid, gizmos and handles by unmounting them on
 * `capturing` — but the capture render is imperative (`renderer.render`) and can
 * race React's commit, so each one also swept the graph for anything tagged
 * `userData.studioHelper` before rendering. That sweep missed the transform
 * gizmo, which is the most visible helper of all: TransformControls is created
 * by drei, not by our JSX, so there is nowhere to hang the tag, and the
 * translate/rotate arrows were baked into captures whenever the unmount lost
 * the race — and then faithfully reproduced by the model in the final render.
 *
 * `isTransformControlsRoot` is three's own flag on the object it puts in the
 * scene (the gizmo and its picker planes hang off it). Flags like that are
 * deliberately stable across minification, unlike a constructor name.
 */
export function hideCaptureHelpers(scene: THREE.Scene): THREE.Object3D[] {
    const hidden: THREE.Object3D[] = [];
    scene.traverse((o) => {
        if (!o.visible) return;
        const isHelper = (o as any).userData?.studioHelper === true
            || (o as any).isTransformControlsRoot === true;
        if (!isHelper) return;
        o.visible = false;
        hidden.push(o);
    });
    return hidden;
}

/**
 * Put back what {@link hideCaptureHelpers} took away — except a gizmo that has
 * since been detached.
 *
 * `TransformControlsRoot.visible` is owned exclusively by three: `attach()`
 * sets it true, `detach()` sets it false, and nothing recomputes it per frame.
 * A capture unmounts the controls (drei detaches on unmount) between the hide
 * and the restore, so restoring unconditionally handed the scene a DETACHED but
 * visible gizmo — arrows stuck on screen for ever, because the only thing that
 * would hide them again is the detach that already ran.
 *
 * Checking `controls.object` distinguishes the two cases exactly: still
 * attached means the hide was ours to undo; detached means three has since
 * taken ownership, and the next `attach()` will show it.
 */
export function restoreCaptureHelpers(hidden: THREE.Object3D[]): void {
    hidden.forEach((o) => {
        const root = o as any;
        if (root.isTransformControlsRoot === true && root.controls?.object === undefined) return;
        o.visible = true;
    });
}
